import * as t from './actionTypes';
import {
  countryCodes,
  dataExportRegions,
  exportTypeTitles,
  exportsOptionOpen,
  inputObjectDefaults,
  timers,
} from './data-constants';

import {
  isOverDataLimit,
  isUnderDataMinimum,
} from './selectors';

const {
  includeCargo,
  includeCodeshares,
  selectedStartDate,
  selectedEndDate,
  userExportIntendedUse,
  userIndustrySelect,
} = inputObjectDefaults;

const initialState = {
  loading: false,
  loaded: false,
  airlines: [],
  arrivalAirports: [],
  departureAirports: [],
  arrivalCountries: [],
  departureCountries: [],
  arrivalRegions: [],
  departureRegions: [],
  exportTypeTitles,
  dataExportRegions,
  category: 'historical',
  countryCodes,
  exportsOptionOpen,
  inputObjectDefaults,
  departureCheckedSearch: 'airport',
  arrivalCheckedSearch: 'airport',
  [includeCargo.name]: includeCargo.value,
  [includeCodeshares.name]: includeCodeshares.value,
  [selectedStartDate.name]: selectedStartDate.value,
  [selectedEndDate.name]: selectedEndDate.value,
  [userExportIntendedUse.name]: userExportIntendedUse.value,
  [userIndustrySelect.name]: userIndustrySelect.value,
  timers,
  canDeleteDataExport: true,
  deletedDataExport: NaN,
  exportToDeleteId: NaN,
  deleteExportModalOpen: false,
  polling: false,
  shouldComponentUpdate: true,
  currentlyGeneratingExportId: null,
  hasBeenFetchedOnce: false,
};

const responseError = (action) => {
  let err = null;

  if (action.error) {
    if (action.error.response && action.error.response.body) {
      err = action.error.response.body.errors || action.error.response.body.error;
    }

    if (action.error.status === 500) {
      err = 'SERVICE_ERROR';
    } else {
      err = err || action.error.message;
    }
  }

  return err;
};

export default (state = initialState, action) => {
  switch (action.type) {
    case t.LOAD_CREATE_EXPORT:
      return {
        ...state,
        createDataExport: null,
        createDataExportError: null,
        createDataExportLoading: true,
      };
    case t.LOAD_CREATE_EXPORT_SUCCESS:
      return {
        ...state,
        createDataExport: action.result,
        createDataExportLoading: false,
        createDataExportError: null,
      };
    case t.LOAD_CREATE_EXPORT_FAIL:
      return {
        ...state,
        createDataExportError: responseError(action),
        createDataExport: null,
        createDataExportLoading: false,
      };
    case t.CLEAR_CREATE_EXPORT:
      return {
        ...state,
        createDataExport: null,
        createDataExportError: null,
        createDataExportLoading: false,
      };
    case t.LOAD_CREATE_EXPORT_FAIL_VALIDATION:
      return {
        ...state,
        createDataExportError: action.result.error,
        createDataExport: null,
        createDataExportLoading: false,
      };
    case t.LOAD_CREATE_EXPORT_CLEAR_ERROR:
      return {
        ...state,
        createDataExportError: null,
      };
    case t.LOAD_CREATE_EXPORT_QUOTE:
      return {
        ...state,
        dataExportQuoteLoading: true,
        dataExportQuoteError: null,
      };
    case t.LOAD_CREATE_EXPORT_QUOTE_SUCCESS:
      return {
        ...state,
        dataExportQuoteLoading: false,
        dataExportQuoteError: null,
        ...action.result,
      };
    case t.LOAD_CREATE_EXPORT_QUOTE_FAIL:
      return {
        ...state,
        dataExportQuoteLoading: false,
        dataExportQuoteError: action.error.response,
        dataExportQuoteCurrentEstimateId: null,
      };
    case t.LOAD_EXPORT_FINAL_QUOTE:
      return {
        ...state,
        dataExportFinalQuoteLoading: true,
        dataExportQuoteError: null,
      };
    case t.LOAD_EXPORT_FINAL_QUOTE_SUCCESS:
      return {
        ...state,
        dataExportFinalQuoteLoading: false,
        dataExportQuoteHistoryLoading: false,
        dataExportQuoteError: null,
        dataExportQuoteCurrentEstimate: action.result.data,
        overDataLimit: isOverDataLimit(action),
        underDataMinimum: isUnderDataMinimum(action),
      };
    case t.LOAD_EXPORT_FINAL_QUOTE_FAIL:
      return {
        ...state,
        dataExportFinalQuoteLoading: false,
        dataExportQuoteError: action.error.response,
        dataExportQuoteCurrentEstimate: null,
      };
    case t.LOAD_DATA_EXPORTS_FOR_USER:
      return {
        ...state,
        shouldComponentUpdate: !state.hasBeenFetchedOnce,
        hasBeenFetchedOnce: true,
        // dont want the spinner for polling, just initial fetch of data
        dataExportQuoteHistoryLoading: (!state.hasBeenFetchedOnce) && true, // spin on first fetch
        dataExportQuoteHistoryError: null,
      };
    case t.DISMISS_QUOTE_REPROCESS:
      return {
        ...state,
        dataExportQuoteHistoryLoading: true,
        dataExportQuoteHistoryError: null,
      };
    case t.LOAD_DATA_EXPORTS_FOR_USER_SUCCESS:
      return {
        ...state,
        dataExportQuoteHistory: action.result,
        dataExportQuoteHistoryLoading: false,
        dataExportQuoteHistoryError: null,
        shouldComponentUpdate: true,
      };
    case t.LOAD_DATA_EXPORTS_FOR_USER_FAIL:
      return {
        ...state,
        dataExportQuoteHistory: null,
        dataExportQuoteHistoryLoading: false,
        dataExportQuoteHistoryError: action.result,
        shouldComponentUpdate: true,
      };
    case t.DISMISS_QUOTE_REPROCESS_FAIL:
      return {
        ...state,
        dataExportQuoteHistory: null,
        dataExportQuoteHistoryLoading: false,
        dataExportQuoteHistoryError: action.error.response,
      };
    case t.LOAD_DATA_EXPORT_TYPES:
      return {
        ...state,
        dataExportTypesLoading: true,
        dataExportTypesError: null,
      };
    case t.LOAD_DATA_EXPORT_TYPES_SUCCESS:
      return {
        ...state,
        dataExportTypesLoading: false,
        dataExportTypes: action.result && action.result.data,
        dataExportTypesError: null,
      };
    case t.LOAD_DATA_EXPORT_TYPES_FAIL:
      return {
        ...state,
        dataExportTypesLoading: false,
        dataExportTypes: {
          categories: null,
        },
        dataExportTypesError: action.error,
      };
    case t.UPDATE_INPUT_VALUE:
    case t.INVALID_INPUT_VALUE:
    case t.RESET_ALL_FORM_FIELDS:
    case t.VALIDATE_FORM_SUBMISSION:
    case t.CLEAR_PREVIOUS_QUOTE:
      return {
        ...state,
        ...action.result,
      };
    case t.SET_SAVED_STATE:
      return {
        ...state,
        ...action.result,
      };
    case t.SET_EXPORT_TYPE:
      return {
        ...state,
        ...action,
      };
    case t.UPDATE_AIRLINE_SELECT:
      return {
        ...state,
        airlines: [...action.result.val],
      };
    case t.UPDATE_AIRPORT_SELECT:
      return {
        ...state,
        [action.result.identifier]: [...action.result.val],
        [action.result.clearError]: null,
      };
    case t.CHECK_DATA_EXPORT_STATUS:
      return {
        ...state,
        checkingDataExportStatus: true,
        dataExportStatus: null,
        checkingDataExportError: false,
      };
    case t.CHECK_DATA_EXPORT_STATUS_SUCCESS:
      return {
        ...state,
        checkingDataExportStatus: false,
        dataExportStatus: action.result,
        checkingDataExportError: false,
      };
    case t.CHECK_DATA_EXPORT_STATUS_FAIL:
      return {
        ...state,
        checkingDataExportStatus: false,
        dataExportStatus: null,
        checkingDataExportError: action.error.response,
      };
    case t.UPDATE_CHECKED_SEARCH:
      return {
        ...state,
        ...action.result,
      };

    case t.UPDATE_SYNC_MULTISELECT:
      return {
        ...state,
        [action.result.identifier]: [...action.result.value],
        [action.result.clearError]: null,
      };

    case t.GET_SERVER_DATE:
      return {
        ...state,
        serverDateLoading: true,
        serverDateError: null,
      };
    case t.GET_SERVER_DATE_SUCCESS:
      return {
        ...state,
        serverDateLoading: false,
        serverDate: action.result,
        serverDateError: null,
      };
    case t.GET_SERVER_DATE_FAIL:
      return {
        ...state,
        serverDateLoading: false,
        serverDate: null,
        serverDateError: action.response.error,
      };
    case t.PREV_EXPORTS_OPEN_OPTION:
      return {
        ...state,
        exportsOptionOpen: action.index,
      };
    case t.CREATE_DATA_EXPORT_EVENT:
      return {
        ...state,
        creatingDataExportEvent: true,
        dataExportEvent: null,
        creatingDataExportEventFail: false,
      };
    case t.CREATE_DATA_EXPORT_EVENT_SUCCESS:
      return {
        ...state,
        creatingDataExportEvent: false,
        dataExportEvent: action.result,
        creatingDataExportEventFail: false,
      };
    case t.CREATE_DATA_EXPORT_EVENT_FAIL:
      return {
        ...state,
        creatingDataExportEvent: false,
        dataExportEvent: null,
        creatingDataExportEventFail: action.error.response,
      };
    case t.DELETE_DATA_EXPORT:
      return {
        ...state,
        deletingDataExport: true,
        deletedDataExport: NaN,
        deletingDataExportFail: false,
        canDeleteDataExport: false,
      };
    case t.DELETE_DATA_EXPORT_SUCCESS:
      return {
        ...state,
        deletingDataExport: false,
        deletedDataExport: action.result && action.result.data && action.result.data.id,
        deletingDataExportFail: false,
        canDeleteDataExport: false,
      };
    case t.DELETE_DATA_EXPORT_FAIL:
      return {
        ...state,
        deletingDataExport: false,
        deletedDataExport: NaN,
        deletingDataExportFail: action.error.response,
        canDeleteDataExport: true,
      };
    case t.REMOVE_DATA_EXPORT:
      return {
        ...state,
        deletedDataExport: NaN,
        exportsOptionOpen: NaN,
        canDeleteDataExport: true,
        dataExportQuoteHistory: action.dataExportQuoteHistory,
      };
    case t.OPEN_DELETE_EXPORT_MODAL:
      return {
        ...state,
        exportToDeleteId: action.id,
        deleteExportModalOpen: true,
      };
    case t.CLOSE_DELETE_EXPORT_MODAL:
      return {
        ...state,
        deleteExportModalOpen: false,
        exportToDeleteId: NaN,
      };
    case t.DISMISS_LIMITED_SEARCH:
      return {
        ...state,
        dataExportQuoteCurrentEstimate: action.result,
      };
    case t.SET_POLLING_STATUS:
      return {
        ...state,
        ...action.result,
      };
    case t.LOAD_REFRESH_EXPORTS:
      return {
        ...state,
        dataExportQuoteHistoryError: null,
        dataExportEvent: null,
        shouldComponentUpdate: false,
      };
    case t.LOAD_REFRESH_EXPORTS_SUCCESS:
      return {
        ...state,
        dataExportQuoteHistory: action.result,
        dataExportQuoteHistoryError: null,
        dataExportEvent: null,
        shouldComponentUpdate: true,
      };
    case t.LOAD_REFRESH_EXPORTS_FAIL:
      return {
        ...state,
        dataExportQuoteHistory: null,
        dataExportQuoteHistoryError: action.error.response,
        dataExportEvent: null,
        shouldComponentUpdate: true,
      };
    case t.RESET_PURCHASE_EXPORT_DATA:
      return {
        ...state,
        ...action.result,
      };
    case t.CLEAR_POLLING_EXPORT_ID:
      return {
        ...state,
        currentlyGeneratingExportId: null,
      };
    default:
      return state;
  }
};



// WEBPACK FOOTER //
// ./modules/DataExports/redux/reducer.js